import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect } from 'react';

/**
 * 2-Stage LOD Timeseries Hook
 *
 * Stage 1: Storage Level Selection (handled by backend)
 *   - Based on time range duration
 *   - 12h session → L4 (10s) by default
 *
 * Stage 2: Pixel Downsample (M4)
 *   - Based on screen width (quantized)
 *   - P_target = clamp(k * Wq, P_min, P_max)
 */

// M4 Bucket structure from backend
export interface M4Bucket {
  start_ts: number;
  end_ts: number;
  min_val: number;
  max_val: number;
  first: number;
  last: number;
  count: number;
  sum_val: number;
}

// API Response structure
export interface TimeseriesResponse {
  stage1_level: number;
  stage1_desc: string;
  stage2_target: number;
  count: number;
  data: M4Bucket[];
}

// LOD Stats structure
export interface LODStats {
  levels: Record<
    string,
    {
      interval_ms: number;
      count: number;
      first_ts?: number;
      last_ts?: number;
      duration_ms?: number;
    }
  >;
  policies: Record<string, { level: number; desc: string }>;
}

// Configuration
const LOD_API_BASE = 'http://localhost:8000';
const MOBILE_WIDTH_THRESHOLD = 480;
const STEP_MOBILE = 50;
const STEP_DESKTOP = 100;

/**
 * Real-time Now Hook
 *
 * Returns current timestamp that updates at the given interval.
 * Essential for real-time time range calculations.
 *
 * @param intervalMs - Update interval in milliseconds (default: 1000ms)
 * @param enabled - Whether to enable the ticker
 */
export function useNow(intervalMs: number = 1000, enabled: boolean = true): number {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!enabled) return;

    const timer = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);

    return () => clearInterval(timer);
  }, [intervalMs, enabled]);

  return now;
}

/**
 * Width Quantization Hook
 *
 * Rounds screen width to step size for cache efficiency:
 * - Mobile (<=480px): step = 50px
 * - Desktop (>480px): step = 100px
 */
export function useQuantizedWidth(width: number) {
  return useMemo(() => {
    const isMobile = width <= MOBILE_WIDTH_THRESHOLD;
    const step = isMobile ? STEP_MOBILE : STEP_DESKTOP;
    const quantized = Math.round(width / step) * step;

    // Calculate point budget
    const K = 4; // M4 factor
    const P_MIN = isMobile ? 300 : 1200;
    const P_MAX = isMobile ? 3000 : 9000;
    const targetPoints = Math.max(P_MIN, Math.min(K * quantized, P_MAX));

    return {
      originalWidth: width,
      quantizedWidth: quantized,
      isMobile,
      targetPoints,
    };
  }, [width]);
}

/**
 * Session Time Management Hook
 *
 * Calculates time range based on session start time.
 * Now accepts an external `now` timestamp for real-time updates.
 *
 * @param sessionStartTime - Session start timestamp (ms)
 * @param now - Current timestamp (use useNow() for real-time updates)
 * @param offsetMs - Offset from session start (default: 0)
 */
export function useSessionTimeRange(
  sessionStartTime: number | null,
  now: number,
  offsetMs: number = 0
) {
  return useMemo(() => {
    if (!sessionStartTime) {
      return { startTs: 0, endTs: 0, durationMs: 0 };
    }

    const startTs = sessionStartTime + offsetMs;
    const endTs = now;
    const durationMs = endTs - startTs;

    return { startTs, endTs, durationMs };
  }, [sessionStartTime, now, offsetMs]);
}

/**
 * Timeseries LOD API Fetch Function
 */
async function fetchTimeseries(
  startTs: number,
  endTs: number,
  widthPx: number,
  forceLevel?: number
): Promise<TimeseriesResponse> {
  const params = new URLSearchParams({
    start: startTs.toString(),
    end: endTs.toString(),
    width: widthPx.toString(),
  });

  if (forceLevel !== undefined) {
    params.append('force_level', forceLevel.toString());
  }

  const response = await fetch(`${LOD_API_BASE}/api/timeseries?${params}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch timeseries: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Latest Timeseries Fetch Function
 */
async function fetchLatestTimeseries(
  windowMs: number,
  widthPx: number
): Promise<TimeseriesResponse> {
  const params = new URLSearchParams({
    window: windowMs.toString(),
    width: widthPx.toString(),
  });

  const response = await fetch(
    `${LOD_API_BASE}/api/timeseries/latest?${params}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch latest timeseries: ${response.statusText}`);
  }

  return response.json();
}

/**
 * LOD Stats Fetch Function
 */
async function fetchLODStats(): Promise<LODStats> {
  const response = await fetch(`${LOD_API_BASE}/api/lod/stats`);

  if (!response.ok) {
    throw new Error(`Failed to fetch LOD stats: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Main 2-Stage LOD Timeseries Hook
 *
 * @param startTs - Start timestamp (ms)
 * @param endTs - End timestamp (ms)
 * @param width - Screen width (will be quantized)
 * @param options - Query options
 */
export function useTimeseriesLOD(
  startTs: number,
  endTs: number,
  width: number,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    forceLevel?: number;
  }
) {
  const { quantizedWidth } = useQuantizedWidth(width);

  return useQuery({
    queryKey: ['timeseries-lod', startTs, endTs, quantizedWidth, options?.forceLevel],
    queryFn: () => fetchTimeseries(startTs, endTs, quantizedWidth, options?.forceLevel),
    enabled: options?.enabled !== false && startTs > 0 && endTs > startTs,
    refetchInterval: options?.refetchInterval,
    staleTime: 0, // Always refetch for real-time updates
    gcTime: 1000, // Garbage collect old queries after 1 second
  });
}

/**
 * Latest Timeseries Hook
 *
 * Fetches latest data within a time window.
 * Useful for real-time chart updates.
 *
 * @param windowMs - Time window from now (e.g., 60000 for last 1 minute)
 * @param width - Screen width (will be quantized)
 * @param options - Query options
 */
export function useLatestTimeseries(
  windowMs: number,
  width: number,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const { quantizedWidth } = useQuantizedWidth(width);

  return useQuery({
    queryKey: ['timeseries-latest', windowMs, quantizedWidth],
    queryFn: () => fetchLatestTimeseries(windowMs, quantizedWidth),
    enabled: options?.enabled !== false && windowMs > 0,
    refetchInterval: options?.refetchInterval ?? 1000, // Default 1Hz polling
    staleTime: 500,
    structuralSharing: true,
  });
}

/**
 * LOD Stats Hook
 *
 * Fetches LOD storage statistics for debugging.
 */
export function useLODStats(enabled: boolean = true) {
  return useQuery({
    queryKey: ['lod-stats'],
    queryFn: fetchLODStats,
    enabled,
    refetchInterval: 5000, // Refresh every 5 seconds
    staleTime: 2000,
  });
}

/**
 * Real-time Session Timeseries Hook
 *
 * Combines session time management with LOD fetching.
 * Automatically calculates time range from session start.
 * Uses refetchInterval for polling without causing re-renders.
 *
 * @param sessionStartTime - Session start timestamp (ms)
 * @param width - Screen width
 * @param options - Additional options
 */
export function useSessionTimeseries(
  sessionStartTime: number | null,
  width: number,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    offsetMs?: number;
  }
) {
  const refetchInterval = options?.refetchInterval === false ? undefined : (options?.refetchInterval ?? 1000);
  const { quantizedWidth, targetPoints, isMobile } = useQuantizedWidth(width);

  // Calculate time range once for initial query key
  // The actual endTs is calculated fresh in queryFn for each refetch
  const startTs = sessionStartTime ? sessionStartTime + (options?.offsetMs ?? 0) : 0;

  const query = useQuery({
    queryKey: ['timeseries-session', sessionStartTime, quantizedWidth, options?.offsetMs],
    queryFn: async () => {
      // Calculate fresh endTs at query time (not render time)
      const freshEndTs = Date.now();
      return fetchTimeseries(startTs, freshEndTs, quantizedWidth);
    },
    enabled: options?.enabled !== false && sessionStartTime !== null,
    refetchInterval: refetchInterval,
    staleTime: 0,
    gcTime: 1000,
  });

  return {
    ...query,
    meta: {
      startTs,
      quantizedWidth,
      targetPoints,
      isMobile,
      sessionStartTime,
    },
  };
}

/**
 * M4 Bucket to Chart Point Converter
 *
 * Converts backend M4 buckets to chart-friendly format.
 */
export function useM4ToChartData(buckets: M4Bucket[] | undefined) {
  return useMemo(() => {
    if (!buckets || buckets.length === 0) {
      return [];
    }

    return buckets.map((bucket) => ({
      time: bucket.start_ts,
      timeFormatted: formatTime(bucket.start_ts),
      value: bucket.last, // Use last value for line chart
      min: bucket.min_val,
      max: bucket.max_val,
      first: bucket.first,
      last: bucket.last,
      count: bucket.count,
      avg: bucket.count > 0 ? bucket.sum_val / bucket.count : 0,
    }));
  }, [buckets]);
}

/**
 * Base Count Offset Calculator
 *
 * Calculates the count value at session start time for offset display.
 * This ensures the chart starts at 0 when the session begins.
 *
 * @param buckets - M4 buckets from backend
 * @param sessionStartTime - Session start timestamp (ms)
 */
export function useBaseCountOffset(
  buckets: M4Bucket[] | undefined,
  sessionStartTime: number | null
) {
  return useMemo(() => {
    if (!buckets || buckets.length === 0 || !sessionStartTime) {
      return 0;
    }

    // Find the bucket at or just before session start time
    // This is the "zero point" for the chart
    let baseValue = buckets[0].first;

    for (const bucket of buckets) {
      if (bucket.start_ts <= sessionStartTime) {
        baseValue = bucket.last;
      } else {
        break;
      }
    }

    return baseValue;
  }, [buckets, sessionStartTime]);
}

/**
 * TPH (Tonnes Per Hour) Calculator
 *
 * Calculates TPH from M4 buckets within a window.
 *
 * @param buckets - M4 buckets
 * @param windowMs - Window size in ms (default: 1000ms for desktop, 1250ms for mobile)
 * @param weightPerFish - Weight per fish in kg (default: 1.45)
 */
export function useTPHCalculation(
  buckets: M4Bucket[] | undefined,
  windowMs: number = 1000,
  weightPerFish: number = 1.45
) {
  return useMemo(() => {
    if (!buckets || buckets.length < 2) {
      return { tph: 0, fishPerMinute: 0 };
    }

    // Find buckets within the window from the end
    const lastBucket = buckets[buckets.length - 1];
    const windowStart = lastBucket.end_ts - windowMs;

    const windowBuckets = buckets.filter((b) => b.start_ts >= windowStart);

    if (windowBuckets.length < 2) {
      return { tph: 0, fishPerMinute: 0 };
    }

    const firstInWindow = windowBuckets[0];
    const lastInWindow = windowBuckets[windowBuckets.length - 1];

    const countDelta = lastInWindow.last - firstInWindow.first;
    const timeDeltaMs = lastInWindow.end_ts - firstInWindow.start_ts;

    if (timeDeltaMs <= 0) {
      return { tph: 0, fishPerMinute: 0 };
    }

    // Fish per minute
    const fishPerMinute = (countDelta / timeDeltaMs) * 60000;

    // Tonnes per hour
    const tph = (fishPerMinute * 60 * weightPerFish) / 1000;

    return {
      tph: Math.max(0, tph),
      fishPerMinute: Math.max(0, fishPerMinute),
    };
  }, [buckets, windowMs, weightPerFish]);
}

/**
 * Time Formatter for MM:SS display
 */
function formatTime(ts: number): string {
  const date = new Date(ts);
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

/**
 * Duration Formatter for HH:MM:SS display
 */
export function formatDuration(durationMs: number): string {
  // Handle negative duration (can occur due to race condition on session start)
  if (durationMs < 0) {
    return '00:00:00';
  }
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Live Duration Hook
 *
 * Returns a live-updating duration string from session start.
 * Only causes re-render of components that use this hook.
 *
 * @param sessionStartTime - Session start timestamp (ms)
 * @param enabled - Whether to enable the ticker
 * @param intervalMs - Update interval (default: 1000ms)
 */
export function useLiveDuration(
  sessionStartTime: number | null,
  enabled: boolean = true,
  intervalMs: number = 1000
): string {
  const now = useNow(intervalMs, enabled && sessionStartTime !== null);
  const durationMs = sessionStartTime ? now - sessionStartTime : 0;
  return formatDuration(durationMs);
}
